const CheckoutType = require('@/lib/Checkout/CheckoutType');
const CustomerDetailsCtrl = require('@/controllers/modal/CustomerDetailsCtrl');
const EmbeddedComponents = require('@/vue/EmbeddedComponents');
const EventNames = require('@/lib/Events/EventNames');
const ItemType = require('@/lib/Items/ItemType');
const ManuallyInputtedTransactionRefStatus = require('@/lib/Payments/ManuallyInputtedTransactionRefStatus');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PaymentPatterns = require('@/lib/PaymentMethods/PaymentPatterns');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const TradingSessionEvent = require('@/lib/TradingSessions/TradingSessionEvent');
const TradingSessionEventType = require('@/lib/TradingSessions/TradingSessionEventType');
const transactionRefStateCollection = require('@/libraries/DigiTickets/Payment/transactionRefStateCollection');
const { cloneDeep } = require('@/functions/clone');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { refundablePaymentsExistForMethod } = require('@/libraries/DigiTickets/Payment/refundablePayments');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');
const { updateAngularUi } = require('@/functions/angular');

/**
 * @param $filter
 * @param $injector
 * @param $modal
 * @param $modalInstance
 * @param $rootScope
 * @param $scope
 * @param $timeout
 * @param {CartService} cartService
 * @param {string} checkoutType
 * @param {CurrentDevice} currentDevice
 * @param {CustomerScreenDataService} customerScreenDataService
 * @param {DialogService} dialogService
 * @param logResource
 * @param {ModalFactory} modalFactory
 * @param {OrderReceiptPrinter} orderReceiptPrinter
 * @param {PaymentService} paymentService
 * @param {PaymentMethodService} paymentMethodService
 * @param {PaymentState} paymentState
 * @param {PrivilegesManager} privilegesManager
 * @param {TradingSessionManager} tradingSessionManager
 * @param {UserService} userService
 * @param {VueModalFactory} vueModalFactory
 * @param {VuexService} vuexService
 */
const PaymentCtrl = function PaymentCtrl(
    $filter,
    $injector,
    $modal,
    $modalInstance,
    $rootScope,
    $scope,
    $timeout,
    cartService,
    checkoutType,
    currentDevice,
    customerScreenDataService,
    dialogService,
    logResource,
    modalFactory,
    orderReceiptPrinter,
    paymentService,
    paymentMethodService,
    paymentState,
    privilegesManager,
    tradingSessionManager,
    userService,
    vueModalFactory,
    vuexService
) {
    $scope.dojoConfig = null;

    /**
     * Watch out - CartService not a Cart!
     *
     * @deprecated - Use the `cartService` variable instead because it has a clearer name.
     * $scope.cartService is set so that it can be used in the template,
     * but in the controller you can just use `cartService`.
     *
     * @type {CartService}
     */
    $scope.cart = cartService;

    /**
     * @type {CartService}
     */
    $scope.cartService = cartService;

    /**
     * @type {boolean}
     */
    $scope.isOfflineCheckout = checkoutType === CheckoutType.NEW_ORDER__OFFLINE;

    /**
     * @type {OrderReceiptPrinter}
     */
    $scope.orderReceiptPrinter = orderReceiptPrinter;

    /**
     * @type {boolean}
     */
    $scope.autoPrintReceiptsEnabled = currentDevice.getDevice().autoPrintReceipts();

    // We need two-way communication between this parent controller and the child controllers for each payment method.
    // If we use $scope.$broadcast we can't listen for responses from child controllers.
    // So instead we collect a reference to all of the child controller scopes here so we can call methods on them.
    // Each child controller must add itself to this object!
    $scope.paymentMethodScopes = {};

    /**
     * @type {CartItemDiscount[]}
     */
    $scope.cartItemDiscounts = $scope.cart.getDiscountSummary();

    /**
     * Used in the template to show/hide the cancel button.
     * Also required by the $scope.$watch('paymentState')
     *
     * @type {PaymentState}
     */
    $scope.paymentState = paymentState;

    $scope.PaymentPatterns = PaymentPatterns;

    $scope.repeatingPaymentModalShown = false;

    $scope.privilegesManager = privilegesManager;

    $scope.allowClose = true;
    $scope.showLoading = false;
    $scope.setLoading = function setLoading(loading) {
        $scope.showLoading = loading;
    };

    /**
     * The thirdPartyID of the payment that is being voided or null.
     *
     * @type {string|null}
     */
    $scope.voidOrRefundInProgress = null;

    $scope.depositsEnabled = false;

    /**
     * Should a transaction be started automatically when switching to the terminal's tab?
     * May be annoying if the user regularly does partial or CNP payments as they have to cancel that,
     * which also prints a receipt.
     *
     * @type {boolean}
     */
    $scope.autoStartCardTransactions = !!currentDevice.getDevice().autoStartCardTransactions;

    /**
     * Payments that have been received.
     *
     * @type {Payment[]}
     */
    $scope.payments = [];

    $scope.getPaymentMethodTemplate = function getPaymentMethodTemplate(paymentMethod) {
        // Remember we load the individual payment method controllers using the data-ng-controller attribute in the
        // partials. Therefore, we can do things like loading alternate controllers for a payment method depending on
        // a variable.
        let templateName = paymentMethod.ref;
        if (paymentMethod.ref === 'card-dojo-chip-and-pin') {
            if (!$scope.dojoConfig) {
                $scope.dojoConfig = $injector.get('dojoConfig');
            }

            if ($scope.dojoConfig && $scope.dojoConfig.isDojoWebPos) {
                templateName = 'card-dojo-webpos';
            }
        }
        return 'partials/modals/payment/methods/' + templateName + '.html';
    };

    /**
     * This is populated by updateAvailablePaymentMethods().
     *
     * @type {PaymentMethod[]}
     */
    $scope.visiblePaymentMethods = [];

    /**
     * This is populated by updateAvailablePaymentMethods().
     *
     * @type {PaymentMethod[]}
     */
    $scope.selectablePaymentMethods = [];

    $scope.PaymentMethodRef = PaymentMethodRef;

    /**
     * Used to update the PaymentCtrl when a numpad is used for displaying the amount in the Received button,
     * as happens on the Cash and Voucher payment methods.
     *
     * @type {number}
     */
    $scope.numPadTotal = 0;

    /**
     * Integrated payment methods like Verifone will tell us when the payment
     * is complete, and then we can add it to the order.
     * But for non-integrated payment methods we need to have a button for the
     * user to confirm that payment has been complete.
     * List the payment methods here that should have this button.
     */
    $scope.showPaymentReceivedButtonForPaymentMethodRefs = [
        PaymentMethodRef.BACS,
        PaymentMethodRef.CARD_MOTO,
        PaymentMethodRef.CARD_OFFLINE,
        PaymentMethodRef.CARD_PAYPAL_HERE,
        PaymentMethodRef.CASH,
        PaymentMethodRef.CHEQUE,
        PaymentMethodRef.FLEXI_VOUCHER,
        PaymentMethodRef.INVOICE,
        PaymentMethodRef.PAYMENT_ON_ACCOUNT,
        PaymentMethodRef.VOUCHER,
    ];

    $scope.showPartialPaymentButtonForPaymentMethodRefs = [
        PaymentMethodRef.BACS,
        PaymentMethodRef.CARD_MOTO,
        PaymentMethodRef.CARD_OFFLINE,
        PaymentMethodRef.CARD_PAYMENT_EXPRESS,
        PaymentMethodRef.CARD_PAYPAL_HERE,
        PaymentMethodRef.CARD_SAGE_PAY,
        PaymentMethodRef.CARD_SAGE_PAY_BACK_OFFICE,
        PaymentMethodRef.CARD_SAGEPAY_SERVER,
        PaymentMethodRef.CHEQUE,
        PaymentMethodRef.INVOICE,
        PaymentMethodRef.PAYMENT_ON_ACCOUNT,
    ];

    $scope.preventCancellingPaymentIfPaymentMethodsPresent = [
        PaymentMethodRef.CARD_MELLON_WEBECR,
    ];

    /**
     * Payment methods where the user can simply click a delete button on the list of payments on
     * the left to remove the payment.
     * Generally this should be things where it's easy to hand back to the customer and remove it. Not ones where
     * a refund would need to be issued (e.g. card payments). We can delete instances of the 3 SagePay payment methods
     * because for these refund methods nothing happens until the refund request is sent for processing in the API.
     *
     * @type {string[]}
     */
    $scope.deletablePaymentMethodRefs = [
        PaymentMethodRef.CARD_SAGE_PAY,
        PaymentMethodRef.CARD_SAGE_PAY_BACK_OFFICE,
        PaymentMethodRef.CARD_SAGEPAY_SERVER,
        PaymentMethodRef.CASH,
        PaymentMethodRef.CHEQUE,
        PaymentMethodRef.FLEXI_VOUCHER,
        PaymentMethodRef.INVOICE,
        PaymentMethodRef.PAYMENT_ON_ACCOUNT,
        PaymentMethodRef.TESCO_VOUCHER_UK,
        PaymentMethodRef.VOUCHER,
    ];

    /**
     * Payment methods where the user can click a delete button on the list of payments on
     * the left to remove the payment by attempting to void it.
     *
     * @type {string[]}
     */
    $scope.voidablePaymentMethodRefs = [
        PaymentMethodRef.CARD_CLOVER_CHIP_AND_PIN,
        PaymentMethodRef.CARD_MELLON_WEBECR,
    ];

    /**
     * Payment methods that allow voiding a payment with a negative amount.
     *
     * @type {string[]}
     */
    $scope.voidableNegativePaymentMethodRefs = [
        PaymentMethodRef.CARD_MELLON_WEBECR,
    ];

    /**
     * Payment methods where the user can click a delete button on the list of payments on
     * the left to remove the payment by attempting to refund it immediately.
     * These will always be payments where the controller is a Vue component.
     *
     * @type {string[]}
     */
    $scope.refundableInProgressPaymentMethodRefs = [
        PaymentMethodRef.CARD_PAYYO_CHIP_AND_PIN,
    ];

    $scope.voidableOrRefundableInProgressPaymentMethodRefs = [
        ...$scope.refundableInProgressPaymentMethodRefs,
        ...$scope.voidablePaymentMethodRefs,
    ];

    /**
     * Payment methods where the operator can edit the payment amount. This doesn't include all the deletable payment
     * methods in many cases it is easier for the operator simply delete the existing payment and add a new one.
     *
     * @type {(string)[]}
     */
    $scope.editablePaymentMethodRefs = [
        PaymentMethodRef.CARD_SAGE_PAY,
        PaymentMethodRef.CARD_SAGE_PAY_BACK_OFFICE,
        PaymentMethodRef.CARD_SAGEPAY_SERVER,
    ];

    /**
     * Sometimes we do not want to show the amount.
     *
     * @type {(string)[]}
     */
    $scope.showRemainingBalanceForPaymentMethodRefs = [
        PaymentMethodRef.BACS,
        PaymentMethodRef.CARD_MOTO,
        PaymentMethodRef.CARD_OFFLINE,
        PaymentMethodRef.CARD_PAYPAL_HERE,
        PaymentMethodRef.CHEQUE,
        PaymentMethodRef.INVOICE,
        PaymentMethodRef.PAYMENT_ON_ACCOUNT,
    ];

    /**
     * These payment methods use a num pad for the user to enter the payment amount.
     * This list is (currently) used to alter the received button text when the user has not typed an amount yet.
     *
     * @type {(string)[]}
     */
    $scope.numPadUsedForPaymentMethodRefs = [
        PaymentMethodRef.CASH,
        PaymentMethodRef.FLEXI_VOUCHER,
        PaymentMethodRef.VOUCHER,
    ];

    /**
     * We log integrated payments. Perhaps we should be logging more here?
     *
     * @type {string[]}
     */
    $scope.loggablePaymentMethodRefs = [
        PaymentMethodRef.CARD_CLOVER_CHIP_AND_PIN,
        PaymentMethodRef.CARD_DOJO_CHIP_AND_PIN,
        PaymentMethodRef.CARD_PAYMENT_EXPRESS,
        PaymentMethodRef.CARD_PAYYO_CHIP_AND_PIN,
        PaymentMethodRef.CARD_VERIFONE_CHIP_AND_PIN,
        PaymentMethodRef.CARD_WORLDPAY_CHIP_AND_PIN,
    ];

    /**
     * Some payment methods modify the cart when used and therefore can't be used when paying an order's balance.
     *
     * @type {(string)[]}
     */
    $scope.paymentMethodsUnavailableWhilePayingBalance = [
        PaymentMethodRef.GOCARDLESS,
        PaymentMethodRef.GIFT_VOUCHER,
        PaymentMethodRef.TESCO_VOUCHER_IRELAND,
        PaymentMethodRef.TESCO_VOUCHER_UK,
    ];

    /**
     * A list of all item types that are compatible with deposits. If an item type is not listed here, and it is
     * present in the cart, it will prevent taking a deposit.
     * DigiTickets Gift Vouchers and Memberships should not be allowed to be purchased using a deposit or zero payment.
     *
     * @type {string[]}
     */
    $scope.depositsPossibleForItemTypes = [
        ItemType.BUNDLE,
        ItemType.DELIVERY_METHOD,
        ItemType.GENERIC,
        ItemType.ITEM_OPTION,
        ItemType.PRODUCT,
        ItemType.PRODUCT_FOOD,
        ItemType.SURCHARGE,
        ItemType.TICKET,
    ];

    /**
     * If there are items in the cart that are not allowed to be purchased via a deposit. Their name will be added
     * to this array, so we can display them in an alert.
     *
     * @type {string[]}
     */
    $scope.itemsBlockingDeposit = [];

    // Add the TaxReceiptToggle Vue component.
    // This doesn't necessarily get displayed. The component will determine itself if it shows.
    $timeout(() => {
        vuexService.getStore().commit('addActiveEmbeddedComponent', { name: EmbeddedComponents.PAYMENT_METHOD_TABS });
        vuexService.getStore().commit('addActiveEmbeddedComponent', { name: EmbeddedComponents.TAX_RECEIPT_TOGGLE });
    });
    $scope.$on('$destroy', () => {
        vuexService.getStore().commit('removeActiveEmbeddedComponent', EmbeddedComponents.PAYMENT_METHOD_TABS);
        vuexService.getStore().commit('removeActiveEmbeddedComponent', EmbeddedComponents.TAX_RECEIPT_TOGGLE);
    });

    $scope.numPadIsUsedForCurrentPaymentMethod = () => {
        return $scope.numPadUsedForPaymentMethodRefs.includes($scope.cart.selectedPaymentMethod.ref);
    };

    /**
     * @param {PaymentMethod} paymentMethod
     *
     * @return {boolean}
     */
    const methodDisabledDueToUncancelableState = (paymentMethod) => {
        return paymentState.paymentModalCancelable === false
            && paymentMethod
            && $scope.cart.selectedPaymentMethod
            && paymentMethod.ID !== $scope.cart.selectedPaymentMethod.ID;
    };

    /**
     * @param {PaymentMethod} paymentMethod
     *
     * @return {boolean}
     */
    const methodDisabledDueToPayingBalance = (paymentMethod) => {
        return cartService.isTakingBalancePayment()
            && $scope.paymentMethodsUnavailableWhilePayingBalance.includes(paymentMethod.ref);
    };

    /**
     * @param {PaymentMethod} paymentMethod
     *
     * @return {boolean}
     */
    const methodDisabledDueToNoRepeatPaymentRequirement = (paymentMethod) => {
        // Disable this payment method if it supports subscriptions and the cart is in a single payment pattern.
        // FIXME: This doesn't seem quite right. Just because it *supports* subscriptions doesn't mean it does not
        // support single payments.
        return (!cartService.currentCart.containsRepeatingPayments() && paymentMethod.supportsSubscriptions);
    };

    /**
     * @param {PaymentMethod} paymentMethod
     *
     * @return {boolean}
     */
    // eslint-disable-next-line unused-imports/no-unused-vars
    const methodDisabledDueToRepeatPaymentRequirement = (paymentMethod) => {
        // Disable this payment method if it does not support subscriptions and the cart requires repeat payments.
        return (cartService.currentCart.containsRepeatingPayments() && !paymentMethod.supportsSubscriptions);
    };

    // Watch for changes in the paymentState singleton because this can have an effect on which payment
    // method tabs are enabled. When this changes we call updateAvailablePaymentMethods to sync the available
    // methods with Vuex.
    $scope.$watch('paymentState', () => {
        updateAvailablePaymentMethods();
    }, true);

    const updateAvailablePaymentMethods = () => {
        const allPaymentMethods = paymentMethodService.getAll();

        const visiblePaymentMethods = filterToVisiblePaymentMethods(allPaymentMethods);
        // Make it available to the Vue components.
        vuexService.getStore().commit('payment/setVisiblePaymentMethods', visiblePaymentMethods);
        // Make it available to the Angular components.
        $scope.visiblePaymentMethods = visiblePaymentMethods;

        const {
            methods: selectablePaymentMethods,
            hasPaymentMethodsDisabledDueToPayingBalance,
            hasPaymentMethodsDisabledDueToRepeatPaymentRequirement,
        } = filterToSelectablePaymentMethods(visiblePaymentMethods);

        // Make it available to the Vue components.
        vuexService.getStore().commit('payment/setSelectablePaymentMethods', selectablePaymentMethods);
        vuexService.getStore().commit('payment/setHasPaymentMethodsDisabledDueToPayingBalance', hasPaymentMethodsDisabledDueToPayingBalance);
        vuexService.getStore().commit('payment/setHasPaymentMethodsDisabledDueToRepeatPaymentRequirement', hasPaymentMethodsDisabledDueToRepeatPaymentRequirement);

        // Make it available to the Angular components.
        $scope.selectablePaymentMethods = selectablePaymentMethods;
    };

    const filterToVisiblePaymentMethods = (paymentMethods) => {
        let methods = paymentMethods.filter((paymentMethod) => {
            const restrictToPaymentMethodRef = vuexService.getStore().state.payment.restrictToPaymentMethodRef;
            if (restrictToPaymentMethodRef && paymentMethod.ref !== restrictToPaymentMethodRef) {
                // Restricted to only one payment method, and this isn't it.
                return false;
            }

            if (paymentMethod.ref === PaymentMethodRef.PAYMENT_ON_ACCOUNT) {
                if (!cartService.currentCart.customer?.account) {
                    // POA is only allowed if an account is selected.
                    return false;
                }

                if (
                    userService.company.settings.restrictPaymentOnAccountToBusinessAccounts
                    && !cartService.currentCart.customer?.account?.isBusiness()
                ) {
                    // POA is only allowed for business accounts.
                    return false;
                }
            }

            return true;
        });

        if ($scope.cart.isRefund()) {
            methods = methods.filter((method) => {
                return (
                    method.supportsNegativePaymentInProPoint
                    || refundablePaymentsExistForMethod(
                        method,
                        cartService.originalOrder?.payments || []
                    )
                ) && method.ref !== PaymentMethodRef.GIFT_VOUCHER;
            });
        } else {
            // For a normal order remove online payment methods that are only for refunds while editing.
            methods = methods.filter((method) => method.showOnEpos);
        }

        return methods;
    };

    /**
     * @typedef {object} SelectablePaymentMethodsResponse
     * @property {PaymentMethod[]} methods The filtered list of payment methods.
     * @property {boolean} hasPaymentMethodsDisabledDueToPayingBalance
     * @property {boolean} hasPaymentMethodsDisabledDueToRepeatPaymentRequirement
     */

    /**
     * @param {PaymentMethod[]} paymentMethods
     *
     * @return {SelectablePaymentMethodsResponse}
     */
    const filterToSelectablePaymentMethods = (paymentMethods) => {
        let hasPaymentMethodsDisabledDueToPayingBalance = false;
        let hasPaymentMethodsDisabledDueToRepeatPaymentRequirement = false;

        const methods = paymentMethods.filter((paymentMethod) => {
            if (methodDisabledDueToUncancelableState(paymentMethod)) {
                return false;
            }

            // Remove methods unavailable if we are taking a balance payment.
            if (methodDisabledDueToPayingBalance(paymentMethod)) {
                hasPaymentMethodsDisabledDueToPayingBalance = true;
                return false;
            }

            // Remove methods unavailable if we require repeat payments (memberships).
            // PROP-2584 - we don't disable these payment methods, we just show a warning.
            // Some clients are intentionally taking payment with a non-recurring payment method for some
            // repeat billing memberships.
            // if (methodDisabledDueToRepeatPaymentRequirement(paymentMethod)) {
            //     hasPaymentMethodsDisabledDueToRepeatPaymentRequirement = true;
            //     return false;
            // }

            // Remove methods unavailable if we do *not* require repeat payments.
            if (methodDisabledDueToNoRepeatPaymentRequirement(paymentMethod)) {
                return false;
            }

            return true;
        });

        return {
            methods,
            hasPaymentMethodsDisabledDueToPayingBalance,
            hasPaymentMethodsDisabledDueToRepeatPaymentRequirement,
        };
    };

    $scope.selectDefaultPaymentMethod = function selectDefaultPaymentMethod() {
        if ($scope.selectablePaymentMethods.length < 1) {
            // No payment methods available.
            $scope.selectPaymentMethod(null);
            return;
        }

        if (cartService.getSelectedPaymentMethod()) {
            // Already a payment method selected.
            return;
        }

        if (cartService.currentCart.containsRepeatingPayments()) {
            // Cart contains repeat payments, so select the first payment method that supports this.
            const repeatingPaymentMethod = $scope.selectablePaymentMethods.find(
                (method) => method.supportsSubscriptions
            );
            if (repeatingPaymentMethod) {
                $scope.selectPaymentMethod(repeatingPaymentMethod);
                return;
            }
        }

        // Select the first payment method.
        $scope.selectPaymentMethod($scope.selectablePaymentMethods[0]);
    };

    $scope.setRestrictToPaymentMethodRef = function setRestrictToPaymentMethodRef(ref) {
        vuexService.getStore().commit('payment/setRestrictToPaymentMethodRef', ref);
        updateAvailablePaymentMethods();
    };

    $scope.clearError = function clearError() {
        $scope.error = {
            exists: false,
            message: '',
        };
    };

    $scope.setError = function setError(msg) {
        if (msg) {
            $scope.error = {
                exists: true,
                message: msg,
            };
        } else {
            $scope.clearError();
        }
    };

    $scope.hasErrors = function hasErrors() {
        return $scope.error.exists;
    };

    $scope.setVoidOrRefundInProgress = function setVoidOrRefundInProgress(value) {
        $scope.voidOrRefundInProgress = value;
    };

    $scope.initialize = function initialize() {
        $scope.depositsEnabled = userService.company.depositsEnabled();

        paymentState.clearPaymentModalCancelable();
        $scope.selectPaymentMethod(null);
        $scope.clearError();

        updateAvailablePaymentMethods();

        // Use a timeout before selecting an initial payment method so the child controllers
        // have a chance to load (and receive the switch-to event). They might add some restrictions to the
        // available methods.
        $timeout(() => {
            const txnInProgress = cartService.getTransactionInProgress();
            if (txnInProgress) {
                // If there's a transaction already in progress, select that payment method agian.
                const paymentMethod = paymentMethodService.getByRef(txnInProgress.paymentMethodRef);
                if (paymentMethod) {
                    $scope.selectPaymentMethod(paymentMethod);
                }
                $scope.setRestrictToPaymentMethodRef(txnInProgress.paymentMethodRef);
            } else {
                // If no transaction is in progress select the default payment method.
                $scope.selectDefaultPaymentMethod();
            }
        }, 100);
    };

    /**
     * Watch for changes to the selected payment method.
     */
    $rootScope.$on(EventNames.PAYMENT_METHOD_CHANGED, () => {
        $scope.clearError();
        $scope.clearCashbackAmount();
        $scope.setCustomerFacingStatus('');

        // Autofocus on first input in payment method.
        $timeout(function () {
            let input = document.querySelector('.dt-payment-method:not(.ng-hide) form input');
            if (input) {
                input.focus();
            }
        }, 100);
    });

    $rootScope.$on(EventNames.SET_VOID_OR_REFUND_IN_PROGRESS, (e, value) => {
        $scope.setVoidOrRefundInProgress(value);
    });

    /**
     * @param {PaymentMethod} paymentMethod
     */
    $scope.selectPaymentMethod = function (paymentMethod) {
        let currentMethod = cartService.getSelectedPaymentMethod();
        if (currentMethod && paymentState.paymentModalCancelable === false) {
            // Cannot switch payment method while transaction is in progress.
            return;
        }
        cartService.setSelectedPaymentMethod(paymentMethod);
    };

    $scope.preventClose = function preventClose() {
        $scope.allowClose = false;
    };

    /**
     * @param {Payment} payment
     */
    $scope.removePayment = async (payment) => {
        await cartService.modifyCurrentCart((cart) => {
            cart.removePayment(payment);
        });
        $scope.$broadcast('payment-modal.payment-removed', payment);
        $rootScope.$broadcast(EventNames.CART_PAYMENT_REMOVED);
        $rootScope.$broadcast(EventNames.CART_PAYMENTS_CHANGED);
    };

    $scope.editPayment = function editPayment(payment, $index) {
        $scope.cart.payments.splice($index, 1);

        $scope.$broadcast('payment-modal.edit-payment-pressed', payment);
        $rootScope.$broadcast(EventNames.CART_PAYMENTS_CHANGED);
    };

    /**
     * Void a payment that has been taken but not yet saved to DigiTickets.
     * Currently only implemented by Clover.
     *
     * @param {Payment} payment
     * @param {number} $index
     */
    $scope.voidOrRefundPayment = (payment, $index) => {
        let message = 'PAYMENT.CONFIRM_VOID';
        if ($scope.refundableInProgressPaymentMethodRefs.includes(payment.ref)) {
            message = 'PAYMENT.CONFIRM_REFUND';
        }

        dialogService.confirm(
            '',
            {
                customHtml: $filter('lang')(message, {
                    amount: payment.amount,
                    lastDigits: payment.lastDigits,
                }),
            }
        ).then((confirmed) => {
            if (confirmed) {
                // Don't update anything just yet, just broadcast the event.
                $scope.$broadcast('payment-modal.void-or-refund-payment-pressed', payment, $index);
            }
        });
    };

    /**
     * Return the number part of paymentCompositeID if it exists.
     * paymentCompositeID is a temporary id in format PAYMENT_METHOD_REF.number e.g. SAGE_PAY_SERVER.3
     *
     * @param payment
     *
     * @returns {string|null}
     */
    $scope.displayPaymentCompositeID = function displayPaymentCompositeID(payment) {
        if (payment.paymentCompositeID) {
            const parts = payment.paymentCompositeID.split('.');
            if (parts[1]) {
                return parts[1];
            }
        }
        return null;
    };

    /**
     * Only needed for non-cash / voucher payments (because we know how much cash was taken).
     * The operator presses this to indicate they have received a payment via the current method, not the full amount.
     * Display the modal to enter how much was received, then add a payment of that amount to the cart.
     */
    $scope.receivedPartial = function () {
        this.showPartialPaymentModal('received')
            .then((result) => {
                if (!result?.value) {
                    return;
                }

                let payment = new Payment();
                payment.setTendered(result.value);
                payment.setPaymentMethod(result.paymentMethod);
                payment.setThirdPartyID(cartService.generatePaymentThirdPartyID());
                payment.setTransactionRef(result.transactionRef);

                if ($scope.cashbackAmount) {
                    payment.setCashback($scope.cashbackAmount);
                }

                $scope.addPaymentAndAutoFinish(payment);
                $scope.clearCashbackAmount();

                if (result.paymentMethod.supportsManualTransactionRef()) {
                    $scope.$broadcast(
                        'payment-modal.clear-transaction-ref',
                        result.paymentMethod
                    );
                }
            })
            .finally(() => {
                updateAngularUi();
            });
    };

    /**
     * False if there is no longer any payments to take (balance is closed).
     *
     * @return {boolean}
     */
    const canReceivePayments = () => {
        if ($scope.isGoCardless()) {
            // GoCardless does not work with balance, so we check if the bank account is selected instead.
            // Return true if one is not selected.
            return !$scope.getSelectedGoCardlessPaymentToken();
        }

        let paymentReceived = false;
        const remainingBalance = $scope.cart.getRemainingBalance();

        if ($scope.cart.isRefund()) {
            paymentReceived = remainingBalance < 0;
        } else {
            paymentReceived = remainingBalance > 0;
        }

        return paymentReceived;
    };

    $scope.paymentReceivedButtonDisabled = () => {
        return !canReceivePayments()
            || ($scope.numPadIsUsedForCurrentPaymentMethod() && $scope.numPadTotal === 0)
            || !isTransactionRefValid();
    };

    /**
     * Generates a payment that can be used to settle the outstanding card balance.
     * The amount of the payment will be the outstanding cart balance and the payment method will be
     * whichever method tab is currently selected.
     * This does not do anything with the Payment (such as add it to the cart) it just returns the new object.
     *
     * @return {Payment}
     */
    $scope.generateSettlementPayment = function () {
        let payment = new Payment();
        payment.setTendered($scope.cart.getRemainingBalance());
        payment.setPaymentMethod($scope.cart.selectedPaymentMethod);
        if ($scope.cashbackAmount) {
            payment.setCashback($scope.cashbackAmount);
        }
        payment.setThirdPartyID($scope.cart.generatePaymentThirdPartyID());

        return payment;
    };

    /**
     * Called when the "Payment Received" button is clicked.
     * By default it will generate a Payment for the remaining cart balance and add that to the cart.
     * The payment method sub-controllers can listen for the 'payment-modal.received-pressed' event and block
     * this behaviour.
     */
    $scope.paymentReceived = function () {
        // Check if any of the sub payment methods want to do anything special.
        // If they do they should return false and then we won't continue to generate and add a payment here.
        let generatePayment = true;
        for (let [key, childScope] of Object.entries($scope.paymentMethodScopes)) {
            if (!childScope.hasOwnProperty('onPaymentReceivedPress')) {
                // This payment method doesn't listen for this event.
                continue;
            }

            if (childScope.onPaymentReceivedPress() === true) {
                // This controller says it's taken care of it. So don't generate a payment.
                console.debug(`${key} controller is overriding default payment received behaviour.`);
                generatePayment = false;
                // Stop calling more child controllers.
                break;
            }
        }

        if (generatePayment) {
            const payment = $scope.generateSettlementPayment();
            $scope.addPayment(payment);
        }

        // Auto "Finish Payment" if there is no longer an outstanding balance.
        // This always happens no matter if the payment was generated by the child controller or by this method.
        $scope.checkRemainingBalanceAndAutoFinishPayment();
    };

    /**
     * Check the cart for any items not allowed to be purchased via a deposit meaning that full payment is required.
     *
     * This updates the value of $scope.itemsBlockingDeposit when called,
     * and returns true if that is now a non-empty array or there are other reasons why a full payment is required.
     *
     * @return {boolean}
     */
    $scope.fullPaymentRequired = () => {
        $scope.itemsBlockingDeposit = [];

        if ($scope.cart.isRefund()) {
            return true;
        }

        $scope.cart.getItems().forEach((item) => {
            if (!$scope.depositsPossibleForItemTypes.includes(item.getItemType())) {
                // An item in the cart is not allowed to be purchased through a deposit.
                $scope.itemsBlockingDeposit.push(item.getName());
            }
        });

        return $scope.itemsBlockingDeposit.length > 0;
    };

    $scope.isGoCardless = () => {
        return cartService.selectedPaymentMethod?.ref === PaymentMethodRef.GOCARDLESS;
    };

    /**
     * Returns the language key to use for the text in the OK/Continue button in the bottom right of the modal.
     * The value depends on if deposits are possible and the payments already taken.
     *
     * @return {string}
     */
    $scope.getContinueButtonLangKey = () => {
        let langKey = 'PAYMENT.SAVE_PAYMENTS';

        if ($scope.depositsEnabled && $scope.cart.getRemainingBalance() !== 0) {
            if ($scope.cart.getRemainingBalance() > 0 && $scope.cart.getTotalTendered() === 0) {
                langKey = 'PAYMENT.CONTINUE_WITHOUT_PAYMENT';
            } else if ($scope.cart.getRemainingBalance() > 0) {
                langKey = 'PAYMENT.CONTINUE_WITH_DEPOSIT';
            }
        }

        if (paymentState.paymentModalCancelable === false) {
            langKey = 'PAYMENT.PAYMENT_IN_PROGRESS';
        }

        return langKey;
    };

    /**
     * Returns a boolean to say if the OK/Continue button in the bottom right of the modal should be disabled.
     * The value depends on if deposits are possible and the payments already taken.
     *
     * @return {boolean}
     */
    $scope.getContinueButtonDisabled = () => {
        if (paymentState.paymentModalCancelable === false) {
            return true;
        }

        if (!$scope.depositsEnabled && $scope.cart.getRemainingBalance() !== 0) {
            return true;
        }

        if ($scope.cart.getRemainingBalance() !== 0 && (!$scope.depositsEnabled || $scope.fullPaymentRequired())) {
            return true;
        }

        return false;
    };

    /**
     * @return {PaymentToken|null}
     */
    $scope.getSelectedGoCardlessPaymentToken = () => {
        return $scope.paymentMethodScopes[PaymentMethodRef.GOCARDLESS].selectedPaymentToken;
    };

    $scope.isGoCardlessContinueButtonDisabled = () => {
        return !$scope.getSelectedGoCardlessPaymentToken();
    };

    /**
     * This shows the same modal as $scope.receivedPartial, but is intended to be used BEFORE a payment is made,
     * to enter the amount that should be used.
     * This is used for integrations such as Payment Express, where the amount can be controlled.
     *
     * @param {('make'|'received')} mode
     * @param {function} callback Gets called with the amount entered.
     */
    $scope.showPartialPaymentModal = function (mode, callback) {
        return vueModalFactory
            .open(
                MODAL_NAMES__EPOS.PARTIAL_PAYMENT,
                {
                    mode,
                    isRefund: cartService.isRefund(),
                    paymentMethod: cartService.getSelectedPaymentMethod(),
                    orderTotal: cartService.getTotalExcludingOriginalOrder(),
                    remainingBalance: cartService.getRemainingBalance(),
                    maxPayment: cartService.getRemainingMaxPayment(),
                }
            )
            .then((result) => {
                if (callback) {
                    callback(result.value);
                }

                updateAngularUi();

                return result;
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    };

    $scope.finalizeAndClose = function () {
        $scope.cart.calculate();

        if ($scope.hasErrors()) {
            return;
        }

        if ($scope.allowClose) {
            $scope.checkRemainingBalanceAndAutoFinishPayment();
        } else {
            // Reset the flag. If they click Ok again, the validation will fire again and set it
            // as necessary.
            $scope.allowClose = true;
        }
    };

    $scope.checkRemainingBalanceAndAutoFinishPayment = function () {
        if ($scope.cart.isRefund()) {
            if ($scope.cart.getRemainingBalance() < 0) {
                return;
            }
        } else if ($scope.cart.getRemainingBalance() > 0) {
            return;
        }

        // Broadcast that the modal is about to go away and there's nothing that can be done to stop it.
        // Better clean up!
        $scope.$broadcast(
            'payment-modal.closing',
            $scope.cart.selectedPaymentMethod ? $scope.cart.selectedPaymentMethod.ref : null
        );

        $modalInstance.close({});
    };

    $scope.handleDeposit = function () {
        // There is no balance to pay. Just continue to finish and close the modal.
        if (!canReceivePayments()) {
            $scope.finalizeAndClose();
            return;
        }

        // There is still a balance to pay, therefore this is either a deposit or zero payment order.
        privilegesManager.requirePrivilege(PrivilegeNames.TAKE_PARTIAL_PAYMENTS).then(
            (privilegeResult) => {
                if (!privilegeResult.granted) {
                    return;
                }

                let confirmMessageKey = 'PAYMENT.DEPOSIT_CONFIRM';
                let confirmMessageProps = { value: $scope.cart.getTotalTendered() };
                let customerRequiredMessage = 'PAYMENT.DEPOSIT_CUSTOMER_REQUIRED';
                if ($scope.cart.getTotalTendered() === 0) {
                    confirmMessageKey = 'PAYMENT.DEPOSIT_CONFIRM_ZERO';
                    confirmMessageProps = null;
                    customerRequiredMessage = 'PAYMENT.DEPOSIT_ZERO_CUSTOMER_REQUIRED';
                }

                const customHtml = $filter('lang')(confirmMessageKey, confirmMessageProps);

                dialogService
                    .confirm(null, {
                        customHtml,
                        cancelLabel: 'CANCEL',
                        okLabel: 'CONTINUE',
                        cancelColor: 'default',
                    }).then(
                        (result) => {
                            if (!result) {
                                return;
                            }

                            // Check cart has account.
                            if (!$scope.cart.customer.hasData()) {
                                const errorMsg = {
                                    langKey: customerRequiredMessage,
                                    type: 'info',
                                };

                                modalFactory.open(
                                    'customer-details',
                                    CustomerDetailsCtrl,
                                    'partials/modals/customerDetails.html',
                                    {
                                        customer: () => cloneDeep($scope.cart.customer),
                                        errorMsg: () => errorMsg,
                                        validateForGiftAid: () => false,
                                        paymentInProgress: () => true,
                                    }
                                ).then((modalResult) => {
                                    // Use the returned customer.
                                    $scope.cart.customer = modalResult.customer;

                                    cartService.recalculateAfterModifyingCartContents().then(() => {});

                                    if ($scope.cart.customer.hasData()) {
                                        $scope.finalizeAndClose();
                                        $modalInstance.close({});
                                    }
                                });
                                return;
                            }

                            $scope.finalizeAndClose();
                            $modalInstance.close({});
                        }
                    );
            }
        );
    };

    const isTransactionRefValid = () => {
        if ($scope.cart.selectedPaymentMethod.manuallyInputtedTransactionRefStatus !== ManuallyInputtedTransactionRefStatus.UNSUPPORTED
            && !transactionRefStateCollection.isTransactionRefValid($scope.cart.selectedPaymentMethod.ref)
        ) {
            return false;
        }
        return true;
    };

    $scope.finishPayment = function () {
        // This method is called when the user clicks the button at the bottom right of the modal. This button is
        // contextual and is the button that you click if you don't click the 'Payment Received' button, for example to
        // handle a zero payment or deposit.
        // Broadcast the fact that "ok" has been pressed, and tell the controllers which tab we're currently on.
        $scope.$broadcast('payment-modal.ok-pressed', $scope.cart.selectedPaymentMethod.ref);
        $scope.handleDeposit();
    };

    $scope.cancelButtonDisabled = () => {
        if (paymentState.paymentModalCancelable === false) {
            return true; // Disable the cancel button.
        }

        const paymentsInCart = cartService.currentCart.payments;
        for (const payment of paymentsInCart) {
            // Disable the cancel button if there are any payments that prevent it.
            if ($scope.preventCancellingPaymentIfPaymentMethodsPresent.includes(payment.paymentMethod.ref)) {
                return true; // Disable the cancel button.
            }
        }

        return false; // Do not disable the cancel button.
    };

    /**
     * @param {boolean} [force]
     */
    $scope.cancel = async function (force) {
        $scope.setCustomerFacingStatus('');

        /**
         * Note that this does *not* include payments from any existing order being edited, only payments
         * taken for the current cart.
         * See mapOrderToCart in mapOrderToCart.js
         *
         * @type {Payment[]}
         */
        const paymentsInCart = cartService.currentCart.payments;
        const numberOfPaymentsInCart = paymentsInCart.length;

        if (!force && numberOfPaymentsInCart > 0) {
            // Payments have already been taken.
            // We need to confirm they want to do this super-bad thing.
            const voidableOrRefundablePaymentsInCart = paymentsInCart.filter((payment) => {
                return $scope.voidableOrRefundableInProgressPaymentMethodRefs.includes(payment.paymentMethod.ref);
            });

            const numberOfVoidableOrRefundablePaymentsInCart = voidableOrRefundablePaymentsInCart.length;
            const numberOfNonVoidableOrRefundablePaymentsInCart = numberOfPaymentsInCart - numberOfVoidableOrRefundablePaymentsInCart;
            const totalAmountOfVoidableOrRefundablePaymentsInCart = voidableOrRefundablePaymentsInCart.reduce(function (prev, cur) {
                return prev + cur.amount;
            }, 0);
            let messages = [];
            let message = '';

            if (numberOfVoidableOrRefundablePaymentsInCart > 0) {
                message = $filter('lang')('PAYMENT.CONFIRM_ABANDON_PAYMENTS_COUNT', {
                    count: numberOfVoidableOrRefundablePaymentsInCart,
                    value: totalAmountOfVoidableOrRefundablePaymentsInCart,
                });
                message += $filter('lang')('PAYMENT.CONFIRM_ABANDON_VOIDABLE_OR_REFUNDABLE_PAYMENTS', {
                    count: numberOfVoidableOrRefundablePaymentsInCart,
                });
                messages.push(message);
            }

            if (numberOfNonVoidableOrRefundablePaymentsInCart > 0) {
                message = $filter('lang')('PAYMENT.CONFIRM_ABANDON_PAYMENTS_COUNT', {
                    count: numberOfNonVoidableOrRefundablePaymentsInCart,
                    value: $scope.cart.getTotalTendered() - totalAmountOfVoidableOrRefundablePaymentsInCart,
                });
                message += $filter('lang')('PAYMENT.CONFIRM_ABANDON_NON_VOIDABLE_PAYMENTS', {
                    count: numberOfNonVoidableOrRefundablePaymentsInCart,
                });
                messages.push(message);
            }

            messages.push('<small>' + $filter('lang')('PAYMENT.CONFIRM_ABANDON_PAYMENTS_COMMON', {
                count: numberOfPaymentsInCart,
            }) + '</small>');

            privilegesManager.requirePrivilege(PrivilegeNames.ABANDON_PAYMENTS).then(
                /**
                 * @param {PrivilegeCheckResult} privilegeResult
                 */
                function (privilegeResult) {
                    if (privilegeResult.granted) {
                        dialogService
                            .confirm(
                                '',
                                {
                                    customHtml: messages.map((html) => `<p>${html}</p>`).join(''),
                                    cancelLabel: 'PAYMENT.CONFIRM_ABANDON_PAYMENTS_CANCEL',
                                    cancelColor: 'default',
                                    okLabel: 'PAYMENT.CONFIRM_ABANDON_PAYMENTS_OK',
                                    okColor: 'danger',
                                }
                            )
                            .then((confirmed) => {
                                if (confirmed) {
                                    // Add a trading session event.
                                    let event = (new TradingSessionEvent(TradingSessionEventType.ABANDON_PAYMENTS))
                                        .setAmount($scope.cart.getTotalTendered())
                                        .setData(paymentService.convertPaymentsToApiStructure(
                                            paymentService.getAllNewPaymentsFromCart(cartService.currentCart)
                                        ))
                                        .setManagerID(privilegeResult.grantedBy);
                                    tradingSessionManager.saveEvent(event);

                                    // Cancel again with the force option.
                                    $scope.cancel(true);
                                }
                            });
                    }
                }
            );

            return;
        } else if (!force && cartService.currentCart.unconfirmedOrderID) {
            // There were no payments taken yet, but an unconfirmed order was created.
            // Ask for confirmation before cancelling the order.
            const confirmed = await dialogService.confirm('PAYMENT.CONFIRM_CANCEL_UNCONFIRMED_ORDER');
            if (!confirmed) {
                // User cancelled the cancelling, so stay here.
                return;
            }
        }

        // Broadcast the fact that "cancel" has been pressed, and tell the controllers which tab
        // we're currently on. Controllers can prevent the closure by setting $scope.allowClose to false.
        $scope.$broadcast(
            'payment-modal.cancel-pressed',
            $scope.cart.selectedPaymentMethod ? $scope.cart.selectedPaymentMethod.ref : null
        );

        if ($scope.allowClose) {
            // Broadcast that the modal is about to go away and there's nothing that can be done to stop it.
            // Better clean up!
            $scope.$broadcast(
                'payment-modal.closing',
                $scope.cart.selectedPaymentMethod ? $scope.cart.selectedPaymentMethod.ref : null
            );

            // No-one prevented dismissing the pop-up, so just do it.
            $modalInstance.dismiss('cancel');
        } else {
            // Reset the flag. If they click cancel again, the validation will fire again and set it
            // as necessary.
            $scope.allowClose = true;
        }
    };

    $scope.showPartialPaymentButton = () => {
        if ($scope.cart.selectedPaymentMethod) {
            return $scope.showPartialPaymentButtonForPaymentMethodRefs.indexOf(
                $scope.cart.selectedPaymentMethod.ref
            ) !== -1;
        }

        return false;
    };

    $scope.showPaymentReceivedButton = () => {
        if ($scope.cart.selectedPaymentMethod) {
            return $scope.showPaymentReceivedButtonForPaymentMethodRefs.indexOf(
                $scope.cart.selectedPaymentMethod.ref
            ) !== -1;
        }

        return false;
    };

    $scope.getPaymentReceivedAmount = () => {
        if ($scope.cart.selectedPaymentMethod) {
            const showRemainingBalance = $scope.showRemainingBalanceForPaymentMethodRefs.indexOf(
                $scope.cart.selectedPaymentMethod.ref
            ) !== -1;

            if (showRemainingBalance) {
                return $scope.cart.getRemainingBalance();
            }

            return $scope.getNumPadTotal();
        }

        return null;
    };

    $scope.setNumPadTotal = function (amount) {
        $scope.numPadTotal = amount;
    };

    $scope.getNumPadTotal = function () {
        return $scope.numPadTotal;
    };

    $scope.showPaymentControlsBar = () => {
        return $scope.showPartialPaymentButton()
            || $scope.showPaymentReceivedButton();
    };

    $scope.getPartialPaymentButtonText = function getPartialPaymentButtonText() {
        if ($scope.cart.isRefund()) {
            return $filter('lang')('PAYMENT.PARTIAL_REFUND_BTN');
        }
        let ref = $scope.cart.selectedPaymentMethod ? $scope.cart.selectedPaymentMethod.ref : null;
        switch (ref) {
            case 'payment-on-account':
                return $filter('lang')('PAYMENT.PARTIAL_PAYMENT_BTN_ACCOUNT');
            default:
                return $filter('lang')('PAYMENT.PARTIAL_PAYMENT_BTN');
        }
    };

    $scope.allowPartialPayments = function allowPartialPayments() {
        return $scope.cart.allowPartialPayments();
    };

    $scope.setCustomerFacingStatus = function setCustomerFacingStatus(status) {
        customerScreenDataService.updateState((state) => {
            state.paymentStatus = status;
        });
    };

    /**
     * Add a payment to the cart.
     * This does not do anything like automatically submit the order. It simply adds the payment to the list.
     *
     * @param {Payment} payment
     */
    $scope.addPayment = function addPayment(payment) {
        console.log('addPayment', payment);
        if (
            payment.paymentMethod && payment.paymentMethod.ref
            && $scope.loggablePaymentMethodRefs.indexOf(payment.paymentMethod.ref) !== -1
        ) {
            /** @type {Payment} */
            let clonedPayment = cloneDeep(payment);
            clonedPayment.paymentMethod = null;
            logResource.storeIntegratedPaymentLog(
                {
                    payment: clonedPayment,
                }
            );
        }

        const paymentEventDefaultData = {
            authCode: payment.authCode,
            paymentMethodID: payment.paymentMethod.ID,
            thirdPartyID: payment.thirdPartyID,
            transactionRef: payment.transactionRef,
            tendered: payment.tendered,
        };

        if (payment.cashback) {
            // Save a cashback trading session event.
            tradingSessionManager.saveEvent(
                (new TradingSessionEvent(TradingSessionEventType.CASHBACK))
                    .setAmount(payment.cashback)
                    .setData(paymentEventDefaultData)
            );
        }

        if (payment.gratuity) {
            // Save a gratuity trading session event.
            tradingSessionManager.saveEvent(
                (new TradingSessionEvent(TradingSessionEventType.GRATUITY))
                    .setAmount(payment.gratuity)
                    .setData(paymentEventDefaultData)
            );
        }

        if (!payment.transactionRef) {
            payment.transactionRef = transactionRefStateCollection.getValidTransactionRef(payment.paymentMethod.ref);
            transactionRefStateCollection.clearState(payment.paymentMethod);
        }
        $scope.cart.addPayment(payment);
        $rootScope.$broadcast(EventNames.CART_PAYMENTS_CHANGED);

        // Clear any success message in the payment state that may be lingering from a previous payment.
        // If a payment method wants to set this based on the payment just added, it should do so after the
        // call to add the payment to the cart.
        paymentState.clearSuccessMessage();
    };

    /**
     * Add a payment to the cart's list of payments.
     * If enough money has been taken after adding the payment, submit the modal.
     *
     * @param {Payment} payment
     */
    $scope.addPaymentAndAutoFinish = (payment) => {
        // This timeout is to the UI updates after adding the payment,
        // because this could be called inside an async function which Angular doesn't like.
        $scope.addPayment(payment);
        updateAngularUi();

        // This timeout is so the user can see the status of the payment before it goes away.
        $timeout(() => {
            $scope.checkRemainingBalanceAndAutoFinishPayment();
        }, 500);
    };

    // This event is emitted by Vue based payment methods.
    $scope.$on(EventNames.ADD_PAYMENT_TO_CART_REQUESTED, (event, payment) => {
        $scope.addPaymentAndAutoFinish(payment);
    });

    /**
     * The amount of cashback requested for the payment currently being taken.
     * This should be set as the cashback amount on the generated Payment object then cleared.
     *
     * @type {float|null}
     */
    $scope.cashbackAmount = null;

    /**
     * Allow cashback to be entered for the current payment?
     *
     * @return {boolean}
     */
    $scope.allowCashback = function allowCashback() {
        if (!(userService.company.maxCashback > 0)) {
            return false;
        }

        return $scope.cart.selectedPaymentMethod && $scope.cart.selectedPaymentMethod.supportsCashback;
    };

    /**
     * Show a modal to enter the cashback amount.
     *
     * @param {function|null} callback
     */
    $scope.enterCashback = function enterCashback(callback) {
        let maxCashback = userService.company.maxCashback;

        dialogService
            .numPad(
                null,
                {
                    initialValue: $scope.cashbackAmount,
                    min: 0,
                    max: maxCashback,
                    title: 'PAYMENT.CASHBACK_TITLE',
                    customText: $filter('lang')('PAYMENT.CASHBACK_MESSAGE', { max: maxCashback }),
                    numPadOptions: {
                        showCashButtons: true,
                        showSignButton: false,
                        allowNull: false,
                        showValueAsCurrency: true,
                    },
                }
            )
            .then(({ value }) => {
                if (value !== null) {
                    $timeout(() => {
                        value = parseFloat(value);
                        $scope.cashbackAmount = value;
                        if (typeof callback === 'function') {
                            callback(value);
                        }
                    });
                }
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    };

    $scope.clearCashbackAmount = function clearCashbackAmount() {
        $scope.cashbackAmount = null;
    };

    $scope.clearManualTransactionRef = () => {
        $scope.transactionRef = null;
    };

    /**
     * Should we display the payment row? Some of them just look wrong, for example a tax at sale experience voucher
     * will display with 0 value because it is already displayed as a discount.
     *
     * @param {Payment} payment
     *
     * @return {boolean}
     */
    $scope.shouldDisplayPaymentRow = (payment) => {
        if (payment.amount === 0 && payment.paymentMethod.ref === PaymentMethodRef.GIFT_VOUCHER) {
            return false;
        }
        return true;
    };

    $scope.reloadCurrentPaymentMethod = () => {
        const currentPaymentMethod = $scope.cart.selectedPaymentMethod;
        $scope.cart.setSelectedPaymentMethod(null);
        $scope.selectPaymentMethod(currentPaymentMethod);
    };

    /**
     * Only display subtotal if there are discounts and there is a discount that is not £0.00
     *
     * @return {boolean}
     */
    $scope.shouldDisplaySubtotal = () => {
        return $scope.cartItemDiscounts.filter((discount) => discount.amount !== 0).length > 0;
    };

    /**
     * @param {CartItemDiscount} discount
     *
     * @return {boolean}
     */
    $scope.shouldDisplayDiscountRow = (discount) => {
        return discount && discount.amount !== 0;
    };

    /**
     * Method to receive TransactionRefState object from the transactionRefDirective for payment methods that do not
     * have their own controller.
     *
     * @param {TransactionRefState} transactionRefState
     */
    $scope.setTransactionRefState = (transactionRefState) => {
        transactionRefStateCollection.setState(transactionRefState);
    };

    /**
     * Does this payment have a transactionRef and if so does it support manual transactionRefs?
     *
     * @param {Payment} payment
     *
     * @return {boolean}
     */
    $scope.hasManualTransactionRef = (payment) => {
        if (
            payment.transactionRef
            && payment.paymentMethod.manuallyInputtedTransactionRefStatus !== ManuallyInputtedTransactionRefStatus.UNSUPPORTED
        ) {
            return true;
        }
        return false;
    };

    // Initialize
    $scope.initialize();
};

module.exports = PaymentCtrl;
